import React, { useMemo, useState } from 'react'

import { t } from 'src/translations'

import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import ChangeLocationModal from 'public/components/default_template/online_ordering/location_selection/ChangeLocationModal'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useOOLocations } from 'public/components/online_ordering/FulfillmentContext'
import { useDisplayPageContext } from '../../../DisplayPageContext'
import { Channel } from 'public/js/siteUtilities'
import { StockIcon } from './StockIcon'

export const LOW_STOCK_THRESHOLD = 5

/**
 * Displays stock availability information for a menu item
 * Shows current location stock status and availability at other locations
 */
export const StockSection = ({ menuItem }: { menuItem?: MenuItem }) => {
  const { channel } = useDisplayPageContext()
  const { selectedLocation, isEcommMultiLocationEnabled } = useRestaurant()
  const ooLocations = useOOLocations()
  const [isLocationStockModalOpen, setIsLocationStockModalOpen] =
    useState(false)

  const stock = menuItem?.stockAcrossLocations?.find(
    (stock) => stock.restaurantGuid === selectedLocation.externalId
  )
  const outOfStock = stock?.outOfStock ?? menuItem?.outOfStock ?? false
  const lowStock = !!stock?.quantity && stock.quantity < LOW_STOCK_THRESHOLD

  // Stock availability message for current location
  const currentLocationStockMessage = useMemo(() => {
    if (outOfStock) {
      return t('product-display.out-of-stock')
    }
    if (!stock?.quantity) {
      return t('product-display.in-stock')
    }
    return t(
      lowStock
        ? 'product-display.low-stock-quantity'
        : 'product-display.quantity-in-stock',
      {
        quantity: Math.floor(stock.quantity)
      }
    )
  }, [outOfStock, stock])

  const stockForEnabledLocations = menuItem?.stockAcrossLocations?.filter(
    (location) =>
      !location.outOfStock &&
      location.restaurantGuid !== selectedLocation.externalId &&
      ooLocations.filter(
        (ooLocation) => ooLocation.externalId === location.restaurantGuid
      ).length
  )

  if (channel !== Channel.ECOMM) {
    return null
  }

  return (
    <div className='stockRow'>
      <div className='stockStatus'>
        <StockIcon outOfStock={outOfStock} lowStock={lowStock} />
        <span className='stockMessage'>{currentLocationStockMessage}</span>
      </div>

      {isEcommMultiLocationEnabled && (
        <>
          <div className='stockAvailability'>
            {stockForEnabledLocations?.length ? (
              <button
                className='locationStockButton'
                onClick={() => setIsLocationStockModalOpen(true)}
                type='button'
              >
                {t('product-display.available-nearby-locations', {
                  count: stockForEnabledLocations.length
                })}
              </button>
            ) : (
              <span className={'noAvailableLocations'}>
                {t('product-display.not-available-at-other-locations')}
              </span>
            )}
          </div>
          <ChangeLocationModal
            isOpen={isLocationStockModalOpen}
            onClose={() => setIsLocationStockModalOpen(false)}
            menuItem={menuItem}
          />
        </>
      )}
    </div>
  )
}
